import * as React from 'react'
import { Link } from 'react-router-dom'

import { Box, Button, PopoverMenu, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { Icon } from '@factorialco/factorial-one-react'
import { PersonAvatar } from '@factorialco/factorial-one-react/dist/experimental'
import { Balance, Clock } from '@factorialco/factorial-one-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { TimePlanningBalance } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

import HoursDisplay, { formatDuration } from './HoursDisplay'
import HoursToPlan from './HoursToPlan'
import PlannedHours from './PlannedHours'
import ShiftsRowCheckbox from './ShiftsRowCheckbox'

interface Props {
  shiftsRow: ShiftsRow
  balances?: TimePlanningBalance
  isOverMaximum: boolean
  plannedHoursTooltipCopy: string
}

const EmployeeInfoCell = ({
  shiftsRow,
  balances,
  isOverMaximum,
  plannedHoursTooltipCopy,
}: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [{ shiftsRows }] = useShiftsRows()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const [hasSeenBanner, setHasSeenBanner] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHideNewHoursToPlanPopover,
    false
  )

  const today = moment()
  const [isHovered, setIsHovered] = React.useState(false)
  const { viewOptions } = useCurrentViewOptions()
  const { navigation } = useNavigation()
  const {
    employee: { showRole, showPlanningBalance, showAvatar, showBankOfHours },
  } = viewOptions
  const hasBankOfHoursEnabled = hasFeatureFlag(
    ShiftManagementFeatureFlags.DEV_TIME_PLANNING_BANK_OF_HOURS
  )

  const visibleRole = !!shiftsRow.job && showRole
  const employee = shiftsRow.employees[0]
  if (!employee) return null

  const [firstName, lastName] = employee.fullName.split(' ')

  const isFirstRow = shiftsRows[0]?.key === shiftsRow.key

  const formatHours = (hours: number | null | undefined) => formatDuration((hours ?? 0) * 3600000)

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" flexGrow minWidth="s0">
      <Box
        flexDirection="row"
        alignItems="flexStart"
        gap="s8"
        justifyContent="center"
        minWidth="s0"
      >
        {showAvatar && (
          <PersonAvatar
            firstName={firstName || ''}
            lastName={lastName || ''}
            size="medium"
            src={buildUrl(employee.avatar)}
          />
        )}
        <Box
          flexDirection="column"
          gap={visibleRole ? 's8' : 's0'}
          paddingLeft={showAvatar ? 's0' : 's8'}
          flexGrow
          minWidth="s0"
        >
          <Box
            flexDirection="column"
            justifyContent="center"
            height={!visibleRole && !showPlanningBalance ? 's32' : 'fit-content'}
            maxWidth="s140"
          >
            <Box flexDirection="row" alignItems="center" gap="s4">
              <Link
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                to={navigation.employees.id.fullPath({ id: employee.id })}
                style={{ textDecoration: 'none' }}
                target="_blank"
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s4"
                  minWidth="s0"
                  maxWidth="s140"
                  flexGrow
                >
                  <Text
                    size="200"
                    weight="medium"
                    color={isHovered ? 'viridian1000' : 'grey1000'}
                    ellipsis
                  >
                    {shiftsRow.title}
                  </Text>
                </Box>
              </Link>
              {isOverMaximum && (
                <TooltipIcon.Small
                  icon="alert-circle"
                  color="radicalRed1000"
                  label={plannedHoursTooltipCopy}
                />
              )}
            </Box>
            {visibleRole && (
              <Text size="100" color="grey800" ellipsis>
                {shiftsRow.job}
              </Text>
            )}
          </Box>
          <Box gap="s8">
            {showPlanningBalance && (
              <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
                <Box flexDirection="row" height="s16" alignItems="center" gap="s4">
                  <Icon icon={Clock} size="xs" className="text-f1-icon-secondary" />
                  <PlannedHours shiftsRow={shiftsRow} />
                  {(shiftsRow.canManage || employee.id === currentEmployee?.id) && (
                    <>
                      <Text size="100" color="grey800">
                        /
                      </Text>

                      {balances ? (
                        <HoursToPlan balances={balances} />
                      ) : (
                        <Skeleton isLoading height="s16" width="s40" />
                      )}
                    </>
                  )}
                </Box>

                {isFirstRow && !hasSeenBanner && (
                  <PopoverMenu type="dialog" open>
                    <PopoverMenu.Trigger asChild>
                      <Box></Box>
                    </PopoverMenu.Trigger>
                    <PopoverMenu.Content side="right" align="center" width="s360" offset={-25}>
                      <Box paddingY="s12" paddingX="s24" flexDirection="column" gap="s24">
                        <Box flexDirection="column" gap="s8">
                          <Text.Header2>
                            {i18n.t('shift_management.hours_information.popover.title')}
                          </Text.Header2>
                          <Text size="200">
                            {i18n.t('shift_management.hours_information.popover.description')}
                          </Text>
                        </Box>
                        <Box alignItems="flexEnd">
                          <Button onClick={() => setHasSeenBanner(true)}>
                            {i18n.t('shift_management.hours_information.popover.actionLabel')}
                          </Button>
                        </Box>
                      </Box>
                    </PopoverMenu.Content>
                  </PopoverMenu>
                )}
              </Box>
            )}
            {hasBankOfHoursEnabled && showBankOfHours && (
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon icon={Balance} size="xs" className="text-f1-icon-secondary" />
                {balances ? (
                  <HoursDisplay
                    title={i18n.t('shift_management.hours_information.bank_of_hours')}
                    totalDuration={formatHours(balances?.bankOfHours ?? 0)}
                    linkTo={navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath(
                      {
                        employeeId: employee.id,
                        periodType: 'weekly',
                        year: today.year(),
                        month: today.month() + 1,
                        day: today.date(),
                      }
                    )}
                    hideBody
                    triggerColor="grey800"
                  />
                ) : (
                  <Skeleton isLoading height="s16" width="s40" />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <ShiftsRowCheckbox shiftsRow={shiftsRow} />
    </Box>
  )
}

export default EmployeeInfoCell
